/**
 * Campaign state — the strategic layer's half of `World`.
 *
 * Every field here is state in the project's sense of the word: it is hashed by
 * `hashWorld`, it is written to the save, and it round-trips. That includes the
 * province graph. The graph *looks* derived — it is built from `src/data` at the
 * start of a campaign — but a burned bridge or a closed pass is a thing that
 * happens during a campaign, and a "cache" whose contents can change what the
 * simulation does is not a cache. So it is state, and it is hashed.
 *
 * Nothing in this file has behaviour. The verbs live in `armies.ts`, `turn.ts`,
 * `ai.ts` and `conquest.ts`.
 */

import type { ResourceBundle } from '../types.ts';
import type { CompanionHero } from '../herotypes.ts';
import type { DiplomacyState } from './diplotypes.ts';
import type { TradeState } from './tradetypes.ts';
import type { MarketState } from './markettypes.ts';
import type { PendingRealmEvent } from './chronicletypes.ts';

/**
 * Where the month's resolution currently is. It is a small state machine rather
 * than one function because a battle can suspend it: the player is handed to
 * battle mode mid-resolution and it picks up exactly where it stopped when the
 * result comes back.
 *
 * With turns gone, `stepRealm` walks this one phase per tick — so a month's
 * business is a fifth of a second of work rather than a single jolt, and a
 * player watching the map sees the marches land before the fights they cause.
 */
export const TurnPhase = {
  /**
   * Not resolving anything: the realm is between councils, which is where it
   * spends all but six ticks of every month.
   *
   * Player orders used to be legal only here. They are legal in every phase now
   * — see the note in `commands.ts` — because a click that lands during a
   * resolution nobody asked for must not be silently thrown away.
   */
  Orders: 0,
  /** Armies march. */
  March: 1,
  /** Contacts are found and fought, one at a time. */
  Battles: 2,
  /** Sieges tighten; garrisons capitulate or hold. */
  Sieges: 3,
  /** Provinces pay. */
  Income: 4,
  /** The AI nations issue their orders for the coming turn. */
  Council: 5,
  /**
   * Hosts already on the roads meet each other, or are run down.
   *
   * Numbered 6 although it runs *first*, before `March`. The order of the turn
   * is the order of the `switch` in `advance`, not the order of these constants,
   * and appending rather than renumbering is what keeps a campaign saved
   * mid-turn by an older build loading into the phase it was actually in.
   */
  Roads: 6,
  /**
   * Fleets meet on the lanes, and then every voyage advances a month.
   *
   * Numbered 7 and run *second*, between `Roads` and `March`, for the same two
   * reasons in the same order. Appending rather than renumbering keeps a
   * campaign saved mid-turn by an older build loading into the phase it was
   * actually in. And contact-before-movement is the same rule `Roads` keeps:
   * two fleets crossing the same lane in opposite directions are only in the
   * same water *while* they are sailing, and a turn that moved them first would
   * let a war fleet and a troop convoy pass through each other.
   *
   * Before `March`, because a landing is a thing hosts do and the hosts that
   * land this month should be caught by the same `Battles` phase as the hosts
   * that walked.
   */
  Seas: 7,
} as const;
export type TurnPhaseId = (typeof TurnPhase)[keyof typeof TurnPhase];

export const TURN_PHASE_NAMES: Record<number, string> = {
  [TurnPhase.Orders]: 'Orders',
  [TurnPhase.March]: 'March',
  [TurnPhase.Battles]: 'Battle',
  [TurnPhase.Sieges]: 'Siege',
  [TurnPhase.Income]: 'Tithe',
  [TurnPhase.Council]: 'Council',
  [TurnPhase.Roads]: 'Roads',
  [TurnPhase.Seas]: 'Seas',
};

/** What an army is currently doing where it stands. */
export const ArmyStance = {
  /** In the field: it will fight, and it will be fought. */
  Field: 0,
  /** Sitting on an enemy holding, waiting for it to starve. */
  Besieging: 1,
  /** Ordered to storm the walls at the next resolution. */
  Assaulting: 2,
} as const;
export type ArmyStanceId = (typeof ArmyStance)[keyof typeof ArmyStance];

/** A block of models of one unit type. The campaign's unit of accounting. */
export interface Regiment {
  /** Index into `UNIT_TYPES`. */
  typeIdx: number;
  models: number;
}

export interface Army {
  id: number;
  /** Named companions travel with this host; tactical handles stay off the realm. */
  heroes: CompanionHero[];
  /** Index into `CampaignState.nations`. */
  owner: number;
  /**
   * Province index it stands in — or, while it is marching, the province it
   * left. An army in transit is *not* in either province: use `armiesStandingIn`
   * when you mean "who is actually here", and `marchesInProgress` when you mean
   * "who is on the roads".
   */
  at: number;
  /** Province index it is marching to, or -1. */
  moveTo: number;
  /**
   * Months of marching left before it arrives.
   *
   * Counted in whole months because that is the unit the realm resolves in — and
   * a month is now a real duration, `MONTH_SECONDS` of simulated time, rather
   * than however long the player took to press a button. A three-month march is
   * fourteen minutes of walking at normal speed, which is what makes a host on
   * the road something you can see coming and do something about.
   */
  moveT: number;
  /**
   * The province this march set out from, or -1 when the army is standing.
   *
   * Redundant with `at` today — a march always starts where the army stands —
   * and deliberately stored anyway, because it is the half of the lane the map
   * needs to draw a counter *between* two provinces and the half interception
   * needs to decide that two hosts are on the same road. Keeping it explicit
   * means turning a march around is a swap of two numbers rather than a special
   * case that has to remember where the army used to be.
   */
  moveFrom: number;
  /** Turns the march was ordered for. `1 - moveT / moveTotal` is progress. */
  moveTotal: number;
  /**
   * Where this host was actually told to go, or -1 when it is where it wanted
   * to be.
   *
   * `moveTo` is the **next province**; this is the **destination**. Before this
   * existed they were the same thing, and that single fact was the whole of
   * *"the current implementation is wierd"*: a march order could only name a
   * province that shared a border with the one the army stood in, so moving a
   * host anywhere real meant issuing one order, waiting out the months, issuing
   * the next, and doing it again — while the map cheerfully let you click a
   * province eight provinces away and simply refused. On a road graph in seven
   * components, where 111 of 150 provinces cannot be reached from the heartland
   * on foot at all, that is not a strategy layer, it is a chore with a refusal
   * attached.
   *
   * With a destination stored, an order is a *destination* and the legs are the
   * simulation's problem: `orderMarch` walks the whole path, takes the first
   * leg, and `doMarch` takes the next one each time a leg lands. Which is what
   * every campaign map the owner has ever played does, and what they meant by
   * "you can freely move them around provinces".
   *
   * Saved with an idempotent `?? -1` rather than behind a `SAVE_VERSION` bump,
   * per the standing rule: the hazard is not an old save, it is a save stamped
   * with the current version written by a build that has never heard of this
   * field.
   */
  dest: number;
  /**
   * Army id this host has been ordered to run down on the road, or -1.
   *
   * Cleared the moment the chase resolves, one way or the other. It is an order,
   * not a relationship: an interception is attempted exactly once.
   */
  intercepting: number;
  /**
   * Months this host is still putting itself back together after a fight on the
   * road, or 0.
   *
   * The one field the running fight needed, and it exists because *nothing*
   * separated two hosts that had just fought. The loser was thrown back one
   * province with its orders untouched, the winner stood still for a month, and
   * the following month both were on the same road again — measured at 42 of
   * every 140 road battles being the same two hosts meeting again, in runs of up
   * to five consecutive months, until one of them died of it. See
   * `settleRoadBattle`.
   *
   * A month of it means: this host does not walk (`doMarch` spends the month on
   * the regrouping instead of the leg), may not be given a march order, may not
   * chase anybody and may not be caught. That last clause is the shattered
   * retreat every game with a strategic map has, and it is what turns a running
   * fight into an event with an outcome rather than a loop.
   */
  regroup: number;
  regiments: Regiment[];
  stance: number;
  /** Turns this army has been sitting on the province it is besieging. */
  siegeTurns: number;
  /** 0..1 — how well fed it is. Below half, it bleeds models every turn. */
  supply: number;
  /** Battles won. Cheap veterancy: it multiplies strength a little. */
  victories: number;
  /**
   * A province this host has set out for and will keep walking toward, or -1.
   *
   * Standing, deliberately. Re-choosing a target every turn made armies dither:
   * a host would announce that it was marching on a capital, then be tempted
   * by whatever was next door on the following turn, and end up pacing between
   * two provinces for the rest of the campaign without ever arriving anywhere.
   * An objective is dropped only when it is reached, becomes unattackable, or
   * the host is no longer strong enough for it.
   */
  objective: number;
  /**
   * Turns this host has stood with nothing to do.
   *
   * A standing army is an argument for using it. Caution is what stops a
   * careful nation throwing hosts at bad odds; impatience is what stops the
   * same nation sitting on a full treasury behind a mountain for a hundred
   * turns, which is the failure every previous attempt to teach `campaignFor`
   * about caution ran into. Reluctance that never decays is indistinguishable
   * from paralysis.
   */
  idleTurns: number;
  /**
   * Fleet id this host is aboard, or -1.
   *
   * A host at sea is *not on the board*: `armiesIn` and `armiesStandingIn` both
   * filter it out, so it cannot defend the province it sailed from, cannot be
   * met there, and cannot be counted as garrison by anybody's arithmetic. Its
   * `at` is left pointing at the port it embarked from and means nothing while
   * this is set — do not read it. What can happen to it is exactly what can
   * happen to the fleet: it arrives, or it drowns.
   */
  aboard: number;
  /**
   * Months this host has stood on ground it has no right to be on.
   *
   * Zero everywhere else, and reset the instant the ground becomes legal again —
   * a grant given back, a war declared, a step taken onto its own soil. It is
   * the clock on the case that arrives constantly and by itself: **a peace
   * strands both sides' armies on each other's territory**, and so does a
   * revoked grant, and so does the end of a war two allies were fighting
   * together.
   *
   * For `TRESPASS_GRACE` months it counts and nothing else happens. After that
   * the host is trespassing: the countryside stops feeding it — `updateSupply`
   * reads this — and the crown whose ground it is thinks worse of its owner
   * every month it stays. It is state rather than something derived from the
   * board, because the board cannot say *how long*: a host that has been
   * squatting since the peace and one that landed there this month look
   * identical in every other field.
   */
  trespassT: number;
  /**
   * This host is *inside* the walls of the holding it stands in.
   *
   * The owner's words: *"army can either fitht open field or you can choose
   * army to hide in the walls, while selecting an army and if you are in a home
   * settlement then let them hide, then you can take them out"*.
   *
   * It is a flag rather than a stance because it is orthogonal to every stance
   * there is: a sheltered host is not besieging, not assaulting, and not in the
   * field, and folding it into `ArmyStance` would have made "field" mean two
   * different things depending on whose ground it was standing on. See
   * `garrisons.ts` for what it costs and what it buys — briefly: it cannot be
   * met in the open, it cannot march until it is brought back out, and it is
   * part of the defence when somebody comes for the walls.
   *
   * Saved with an idempotent `?? false` and hashed, per the standing rule for
   * this file: the hazard is not an old save, it is a save stamped with the
   * current version by a build that has never heard of this field.
   */
  sheltered: boolean;
  name: string;
}

/**
 * A fleet: hulls, and whatever they are carrying.
 *
 * One number for the ships rather than two — warships and transports — because
 * the interesting decision is *how many*, and splitting it in half only makes
 * the player solve a small packing problem before every voyage. A hull fights
 * for `SHIP_POINTS` and carries `SHIP_CARRY` of army, so a fleet big enough to
 * move a real host is also a fleet worth meeting at sea, which is the tension
 * that makes interception matter: everything you own is in the same place.
 *
 * The lane structure mirrors an `Army`'s march deliberately. A voyage is a
 * march on water — origin, destination, total, remainder — so the map draws it
 * with the same machinery, it can be turned around by the same reasoning, and
 * two fleets crossing the same lane meet on it exactly as two hosts on a road
 * do.
 */
export interface Fleet {
  id: number;
  owner: number;
  /**
   * The coastal province it lies off — or, while under way, the one it sailed
   * from. Always a valid province index.
   */
  at: number;
  /** Province it is sailing to, or -1. */
  moveTo: number;
  /** The near end of the lane while under way, or -1. */
  moveFrom: number;
  /** Months of sailing left. */
  moveT: number;
  /** Months the voyage was ordered for. `1 - moveT / moveTotal` is progress. */
  moveTotal: number;
  /** Fleet id this one has been ordered to run down at sea, or -1. */
  intercepting: number;
  /** Hulls. Strength and capacity both come off this one number. */
  ships: number;
  /** Army ids embarked, in the order they came aboard. */
  cargo: number[];
  name: string;
}

/**
 * An undirected sea route between two coastal provinces. `a < b`, always.
 *
 * State rather than content, for the same reason the road graph is: a lane is a
 * thing that can be closed. Nothing closes one today, and the field is hashed
 * and saved anyway — the alternative is a "cache" that the simulation reads and
 * that a future blockade would silently make observable, which is the exact
 * mistake `COORDINATION.md` records having been made twice already.
 */
export interface SeaLane {
  a: number;
  b: number;
  /** Months under sail. */
  months: number;
}

export type HoldingKind = 'city' | 'village';
export type VillageType = 'farming' | 'woodcutting' | 'stone_quarry' | 'iron_mining' | 'fishing' | 'soul_shrine' | 'gold_mining';

export interface ProvinceState {
  /** Permanent settlement identity. Upgrading a village never changes its kind. */
  holdingKind: HoldingKind;
  /** Fixed village industry, empty on a manually managed city. */
  villageType: VillageType | '';
  /** Passive goods awaiting an owned road or warehouse space. Never a second city. */
  villageStock: Partial<ResourceBundle>;
  /** Nation index, or -1 for unclaimed ground. */
  owner: number;
  /** Static defenders, in strength points. Regrows toward its cap when held. */
  garrison: number;
  /** 0..3, or 0..4 once the province is a citadel. Raises the garrison cap and
   * the defender's battle multiplier. See `maxFortAt` in `holdings.ts`. */
  fort: number;
  /** 0..1. Freshly conquered ground pays badly and revolts. */
  unrest: number;
  /** Multiplier on everything the province yields. */
  development: number;
  /**
   * 1..10. What the holding has been *built into*, as against what it was born
   * as.
   *
   * Deliberately a second number rather than a redefinition of `development`.
   * `development` is what the ground and the player's own city amount to — it is
   * written by `syncCapitalDevelopment` off the buildings actually standing in
   * the home world, and it is not something a crown can spend on. The tier is
   * the ladder: ten steps, bought from the world map with the materials of the
   * city that supplies the province, each one dearer than the last, several of
   * them changing what the holding *is* rather than merely what it pays. The two
   * multiply. See `holdings.ts` for the whole of it.
   */
  tier: number;
  /**
   * Tier the works underway are building toward, or 0 when nothing is being
   * built.
   *
   * A tier is not bought, it is *built*: the materials leave the supplying city
   * the month the order is given and the tier lands months later. That is what
   * makes development something visible happening on a map that now runs in real
   * time, rather than a number that jumps when a button is pressed.
   */
  worksTo: number;
  /** Months of building left. Meaningless when `worksTo` is 0. */
  worksT: number;
  /**
   * The crown that ordered the works, or -1.
   *
   * Stored rather than assumed to be `owner`, because the interesting case is
   * the one where they differ: a province that changes hands mid-works has had
   * somebody else's masons on it, and what happens then — the works stop, the
   * materials are gone — is a rule rather than an accident.
   */
  worksBy: number;
  /** Nation index currently besieging, or -1. */
  siegeBy: number;
  /** Turns the siege has been running. */
  siegeT: number;
  /**
   * The crown that held this ground before whoever holds it now, or -1.
   *
   * This is the game's entire claim system, and it is one number rather than
   * EU4's per-nation core flags because one number answers the only question
   * anybody asks of it: *who has a grievance about this province.* A claim is the
   * memory of the last owner, it gives them a `Reconquest` casus belli, and it
   * lasts exactly until somebody else takes the ground — at which point the
   * grievance is theirs instead. Ground that has changed hands three times is not
   * owed to three different crowns; it is owed to whoever lost it last, which is
   * also who is still angry about it.
   */
  claimedBy: number;
  /**
   * Harbour works, 0..`MAX_PORT`. Only a coastal province may have any.
   *
   * A port is what makes a coast worth holding, and it does three separate
   * things so that it is worth the price: a fleet may be raised here, a host may
   * be put aboard one here, and the province earns on the traffic. Nothing else
   * on the map lets an army leave the road network, so the first of those is the
   * one that changes the shape of a campaign — a crown with no port has no
   * answer to a crown with ships except to be somewhere else.
   */
  port: number;
  /**
   * Points of standing garrison this holding's own military works are worth.
   *
   * The other half of *"you can increase it with buildings and age"*. A crown's
   * holdings grow their watch through the tier ladder, which is the only
   * "building" an AI province has; the **player's capital** is a real settlement
   * with real barracks and real towers standing on real ground, and this is
   * where that gets to matter on the strategic map. Written by the capital sync
   * — `capitalMeasure` in `homefront.ts`, through `Cmd.SetCapitalStanding` in a
   * room — and zero everywhere else.
   *
   * Stored rather than derived because the settlement world it is derived *from*
   * is not loaded while you are looking at the map, and half the time does not
   * exist at all: an AI crown has no city world to read. A number the realm
   * carries is the only honest shape.
   */
  barracks: number;
  /**
   * The turn the player first stood in this holding as a place, or 0.
   *
   * *"i took over a town how do i go into it to build"*. A holding you own is
   * ground you can walk into, and the first time you do, a whole settlement
   * world is generated for it — its country's ground, its rung's town, your
   * crown's Age — and kept for the rest of the campaign. See
   * `campaign/holdworld.ts`.
   *
   * **State, not a cache, and the distinction is the project's fourth rule.**
   * Whether a world exists here changes what happens: a settled holding ticks
   * its own economy every frame, writes its own `development` back to this
   * province when you leave it, and is what an assault on this ground is fought
   * in. A field that decides all three is not something the save is allowed to
   * forget, so it is hashed and saved with the rest of the province.
   *
   * A turn number rather than a flag because it is free and it answers the one
   * question a flag cannot: *how long has this been mine as a town*. Zero is
   * "never entered", which is every province on the board on turn one bar the
   * player's own seat.
   */
  settled: number;
  /**
   * How much of the town here is to come down the next time the player stands
   * in it, 0..1.
   *
   * *"you need to rebuild the other town after battle"*, and it is a message
   * from the moment of capture to the moment of arrival rather than a standing
   * property of the ground. A holding stormed by anybody is written with a
   * wreck here; when the player next walks in, `Cmd.WreckTown` brings that
   * fraction of the roofs down into rubble, kills the same share of the people
   * and knocks the walls about, and `Cmd.EnterHold` clears the number so the
   * same sack cannot be paid for twice.
   *
   * Deliberately pending damage rather than damage: at the moment a province
   * changes hands there may be no settlement world for it at all — the player
   * has never been there — and there is nothing to break. Recording the debt on
   * the province is the only shape that is correct both for a town you have
   * built up over a campaign and for one you have never seen.
   */
  wreck: number;
}

/**
 * A city: a province with a warehouse.
 *
 * The realm's materials used to live in one abstract national heap, which meant
 * a crown's timber was equally available at every point on a board a hundred and
 * twenty-seven provinces wide. A city is the repair. It is a *place* where goods
 * pile up: every province sends what it produces to the nearest city it can
 * reach over its own roads, and everything that is built out of materials rather
 * than out of coin is built out of a particular city's yard.
 *
 * Coin is not here, and that is the distinction the whole system turns on. Gold
 * travels — a letter of credit weighs nothing — so the treasury stays national.
 * Timber does not travel, and a province four weeks from the nearest city is
 * four weeks of wagons dearer to build anything on.
 *
 * Every nation's seat is a city from the first month. Others are chartered by
 * building a holding up to tier six, which is the single most useful thing a
 * long campaign can do with a frontier: it puts a warehouse where the frontier
 * is, and everything around it becomes cheap.
 */
export interface CityState {
  /** Province index. Unique across `cities`. */
  at: number;
  /**
   * The crown whose city this is, as of the last monthly reconciliation.
   *
   * Kept rather than read off the province, because the difference between the
   * two is how a sack is detected: a city whose province is held by somebody
   * else has been taken, and a taken warehouse is emptied — half into the
   * captor's treasury and half into the mud.
   */
  owner: number;
  /** What is in the yard. Capped per resource; see `cityStockCap`. */
  stock: ResourceBundle;
  /** Turn the charter was granted. Seats of the realm are founded on turn 1. */
  founded: number;
}

/** An undirected edge of the province graph. `a < b`, always. */
export interface ProvinceLink {
  a: number;
  b: number;
  kind: number;
  /** Turns to march across. Stored, because a broken road is a state change. */
  march: number;
}

export interface Nation {
  /** Index into `NATIONS`. Kept explicit so the array can be reordered safely. */
  def: number;
  treasury: ResourceBundle;
  manpower: number;
  alive: boolean;
  /**
   * 1 where this nation is at war with the nation at that index.
   *
   * Still here, still hashed, still saved — and no longer the definition of a
   * war. It is the O(1) index over `CampaignState.diplo.wars`, rebuilt in full by
   * `refreshWarFlags` whenever any war changes, and it exists because everything
   * hot in this layer asks "may I shoot at them" inside a loop: the contact scan,
   * supply, march legality and the AI's threat arithmetic. Nothing but
   * `refreshWarFlags` may write it.
   */
  atWar: number[];
  /**
   * -100..100. What this crown thinks of the crown at that index.
   *
   * Deliberately asymmetric — what you think of them and what they think of you
   * are two numbers — and deliberately *stored* rather than derived. Each turn it
   * drifts toward `baseOpinion`, which is read off the board; events knock it
   * about on the way. That is what lets a crown be your natural friend by faith
   * and geography and still not have forgiven you for last decade.
   */
  relations: number[];
  /**
   * Index into `CONFESSIONS`. What this crown believes.
   *
   * State rather than a property of the nation table, because a crown's faith is
   * a thing that can move — and because the nation table is being rewritten by
   * somebody else this week. `startingConfession` is where it comes from.
   */
  confession: number;
  /** Nation index this crown answers to, or -1. */
  liege: number;
  /** A `SubjectKind`. Always `None` when `liege` is -1. */
  subject: number;
  /** Turn the oath was sworn. Annexation is a matter of time as well as opinion. */
  subjectSince: number;
  /**
   * What the four interests inside this crown think of it. `ESTATE_COUNT` long,
   * indexed by `Estate`, each -100..100.
   *
   * A crown used to be a single will with a treasury: the only machinery inside
   * one was a court that moved when a capital fell, so nothing at home could ever
   * tell a crown *no*. These are the smallest true repair — the hosts, the
   * temple, the counting houses and the great families, each with one opinion and
   * each owning exactly one lever on the board. See `estates.ts` for what moves
   * them and what each one costs or grants.
   *
   * Stored rather than derived, and that is the whole point: the standing is
   * *path-dependent*. It drifts toward a target read off the board, and the
   * answers a crown gives to the realm's chronicle knock it about on the way —
   * so two crowns with identical boards can have completely different politics
   * because of what they each decided in year four.
   */
  estates: number[];
}

/**
 * A battle the campaign has decided must happen but has not yet resolved.
 *
 * This is the whole seam between the two layers. The campaign produces one of
 * these; battle mode consumes it (via `battleConfigFor`) and hands back an
 * outcome and two survival fractions. The tactical simulation is never
 * duplicated and never even imported at runtime.
 */
/**
 * What is actually at stake in a pending battle.
 *
 * `openBattle` used to describe exactly one situation — a host standing on a
 * province somebody else holds — and winning meant taking the ground. Two more
 * situations exist now and neither of them is about ground: a fight on a road
 * between two provinces, and a fight against brigands squatting on a province
 * that may well already belong to the attacker. Rather than teach
 * `applyBattleResult` to guess from the surrounding state, the battle says which
 * of the three it is, and the one description of "what winning does" branches on
 * it once, in the open.
 */
export const BattleKind = {
  /** For a province. The winner holds it. */
  Province: 0,
  /** On the road between two provinces. Nothing changes hands; the loser goes home. */
  Road: 1,
  /** Against a bandit camp. The winner clears it and loots it; no ground moves. */
  Camp: 2,
  /**
   * There is no `Sea`, and that is a decision rather than an omission.
   *
   * A `BattleKind` exists to tell `applyBattleResult` what winning does, and it
   * is only ever reached through `PendingBattle` — which exists for exactly one
   * purpose: suspending the turn while the player fights something by hand. A
   * fight between two fleets cannot be fought by hand, because battle mode is a
   * land game and giving it ships is a different project. So a sea battle is
   * settled where it happens, in `fleets.ts`, through the shared `rollBattle`,
   * and never becomes state. See `resolveSeaContact`.
   */
} as const;
export type BattleKindId = (typeof BattleKind)[keyof typeof BattleKind];

/**
 * The nation index brigands answer to, which is none.
 *
 * -1 already means "unclaimed ground" everywhere in this layer, so camps needed
 * a value of their own rather than a reuse that would have made an unowned moor
 * and a bandit host indistinguishable in `PendingBattle.defenderNation`.
 */
export const BANDIT_NATION = -2;

export interface PendingBattle {
  province: number;
  /** A `BattleKind`. Decides what winning does. */
  kind: number;
  /**
   * The other end of the road, for a `BattleKind.Road` fight; -1 otherwise.
   * `province` carries the end the attacker set out from.
   */
  roadTo: number;
  /**
   * Army id that went out to force a road contact, or -1 for a head-on meeting.
   *
   * Stored on the battle rather than read back off the army, because by the time
   * the result comes in the army's `intercepting` has been cleared — and because
   * a battle that was fought by hand may come back several minutes and one save
   * file later.
   */
  interceptor: number;
  attackerNation: number;
  defenderNation: number;
  /** Army ids. The defender may be -1 when only a garrison stands there. */
  attackerArmy: number;
  defenderArmy: number;
  /** Strength points each side brings, after terrain and fortification. */
  attackerPoints: number;
  defenderPoints: number;
  /** A `ScenarioKind` from battle mode. */
  scenario: string;
  /** True when the player is on one side and may fight it by hand. */
  playerFights: boolean;
  /** True when the player is the attacker. Decides who "won" means. */
  playerAttacks: boolean;
  /** Seed for the tactical world, or for the abstract resolution. */
  seed: number;
}

/**
 * Brigands on a province: neutral, hostile to everyone, and a tax.
 *
 * A camp is not a nation and does not march. It sits on one province, takes a
 * cut of everything that province produces, whittles the garrison, and — if it
 * is left alone long enough — puts a second camp on the province next door.
 * That is the whole behaviour, and it is deliberately the whole behaviour: the
 * point of camps is to give an opening campaign something to do that is not
 * "declare on a nation", not to add a ninth power to the board.
 *
 * At most one camp stands on a province, so `at` is the key.
 */
export interface BanditCamp {
  /** Province index. Unique across `camps`. */
  at: number;
  /** Strength points it fights with, and what it costs to be rid of it. */
  strength: number;
  /** Turn it appeared. Age is what drives spreading. */
  founded: number;
  /**
   * Turns of paid-for pressure left on it, from `Cmd.CampaignBounty`.
   *
   * A bounty is how a nation with no army in the field answers a camp: gold
   * instead of a battle, slower and more expensive than marching on it, and
   * available on turn one when marching on it is not.
   */
  bounty: number;
}

/** What kind of trouble a warning is about. */
export const WarnKind = {
  /** A hostile host is on a road, and the far end of that road is yours. */
  March: 0,
  /** A hostile host stands on ground next to yours, with no orders yet visible. */
  Massing: 1,
  /** Brigands are sitting on ground you hold. */
  Bandits: 2,
  /**
   * A hostile fleet with a host aboard is on a lane, and the far end is yours.
   *
   * The fairness rule — nothing arrives that was not on the player's map first —
   * was written when the only way to reach somebody was to walk, and a landing
   * would have broken it outright: ships are two to seven times faster than a
   * road, so a host could appear on a coast the player had never seen it
   * approach. This is the repair, and it is the same repair `March` is: the
   * voyage is visible for its whole length.
   */
  Landing: 3,
} as const;
export type WarnKindId = (typeof WarnKind)[keyof typeof WarnKind];

/**
 * Something the player has been *told*, before it happens to them.
 *
 * `director.ts` states the project's fairness rule outright: everything the
 * director decides is visible before it happens, and difficulty comes from the
 * player's choices rather than from surprise. The world map broke it — a host
 * could set out and arrive with nothing on screen in between — and this is the
 * repair. A warning is state, not a notification: it is hashed, it is saved, and
 * `sinceTurn` records when it was first raised, which is what makes "you were
 * warned before it landed" a property a test can assert rather than a claim.
 *
 * Warnings are kept for the player's nation only. They are the player's
 * intelligence, not a global fact about the board; an AI nation reasons from the
 * board itself in `ai.ts` and needs no copy.
 */
export interface MarchWarning {
  /** A `WarnKind`. */
  kind: number;
  /** Nation marching, or `BANDIT_NATION`. */
  nation: number;
  /** Army id, or -1 for a bandit camp. */
  armyId: number;
  /** Where it is coming from — the near end of the road, or where it stands. */
  from: number;
  /** Where it will arrive. For `Bandits`, the province they sit on. */
  to: number;
  /** Turns until it arrives. 0 means "already on your doorstep". */
  turns: number;
  /** Strength points, as scouted. Exact: this layer does not lie to the player. */
  strength: number;
  /** The turn this warning was first raised. */
  sinceTurn: number;
}

export interface CampaignState {
  turn: number;
  phase: number;
  /** Nation index the player commands. */
  playerNation: number;
  /**
   * Every crown a human holds, this one included.
   *
   * `playerNation` answers "whose is the sheet on the right" and was enough
   * while exactly one person played. A room seats several, each on their own
   * crown, and the questions that used to be the same question are not: the
   * AI must leave *all* of them alone, while the UI still needs to know which
   * one is reading it. So the set is state — shared, hashed, identical on
   * every client — and which of them is *you* is a local view, and lives in
   * the client rather than in here.
   */
  humanNations: number[];
  /**
   * The province holding the player's actual, built city.
   *
   * Distinct from the nation's `capital` in the data table because it is a
   * property of *this campaign*: it is the one province whose assault is fought
   * in the home world rather than on an abstract field, and it does not move
   * when the nation's fortunes do. If it is ever lost, the run is over — there
   * is no second Hearth.
   */
  capital: number;
  nations: Nation[];
  provinces: ProvinceState[];
  /** The graph. State, not a cache — see the note at the top of this file. */
  links: ProvinceLink[];
  armies: Army[];
  nextArmyId: number;
  /**
   * Hulls on the water. Ordered by id, like the armies, so iteration is stable.
   */
  fleets: Fleet[];
  nextFleetId: number;
  /**
   * The sea's road graph, read off the water by `src/data/realm.ts`.
   *
   * Here for the same reason `links` is here rather than being read out of the
   * data table on every query: a lane is a thing that could be closed, and the
   * moment anything can close one it is state. Nothing closes one today. The
   * field is hashed and saved anyway, because the alternative is a derived
   * cache that a future blockade would silently make observable — which is
   * exactly the mistake `COORDINATION.md` records having been made twice.
   */
  lanes: SeaLane[];
  /** Brigand camps, ordered by province index so iteration is stable. */
  camps: BanditCamp[];
  /**
   * Warehouses. Ordered by province index so iteration — and therefore the
   * hash, the save and every draw of materials — is stable.
   */
  cities: CityState[];
  /** What the player's scouts have seen, rebuilt every turn. Player nation only. */
  warnings: MarchWarning[];
  /**
   * Wars, treaties, truces and everything sitting on somebody's table.
   *
   * Sparse lists rather than matrices, and that is the scale decision: the
   * opinion table has to be `n²` because every crown has a view of every other
   * crown, but there will only ever be a few dozen live treaties on a board of
   * thirty kingdoms, and walking a few dozen is linear in what exists rather than
   * in what could.
   */
  diplo: DiplomacyState;
  /**
   * The trade graph, who controls what, and everybody's merchants.
   *
   * The node graph inside it is state on exactly the argument `links` is: it is
   * grown from the province table at the start of a campaign, it is thereafter
   * never rebuilt, and two campaigns whose arrows point different ways are two
   * different campaigns. See the note at the top of `tradetypes.ts` for what was
   * taken from the EU4 model and what was left out of it.
   */
  trade: TradeState;
  /**
   * The free market's order book: goods on sale at chosen prices, standing
   * bids, and the id counter.
   *
   * State in the fullest sense — a sell order is *escrow*, goods that have
   * already left a treasury and exist nowhere but on the order — so two
   * campaigns that differ only in an open order differ in what the next
   * council settles. Hashed and saved in full. Shapes and the escrow argument
   * live in `markettypes.ts`; the matching rule in `market.ts`.
   */
  market: MarketState;
  pending: PendingBattle | null;
  /**
   * A question the realm has put to the *player*, or null.
   *
   * State in exactly the sense `pending` is, and it stops `stepRealm` for exactly
   * the same reason: a question the player is looking at must not be a UI pause,
   * because a UI pause is a fact about how long somebody stared at a screen and
   * two replays that stared for different lengths of time would diverge. Only
   * ever the player's — the twenty-nine other crowns answer theirs on the spot,
   * inside the council that drew it. See `chronicleturn.ts`.
   */
  pendingEvent: PendingRealmEvent | null;
  /**
   * Every answer every crown has given, newest last, as `nation:event>choice`.
   *
   * Crown-scoped keys, so two crowns do not share a memory. It is the repeat
   * counter, the chain predicate and the save record in one field — the same
   * trick `w.eventHistory` plays in the settlement — which is why a consequence
   * arriving two years after the decision that caused it costs no new state and
   * cannot desynchronise.
   *
   * Bounded at `MAX_EVENT_HISTORY`, because it is hashed and written into every
   * save of a campaign that runs for tens of hours.
   */
  eventHistory: string[];
  /** Bounded chronicle of the campaign, newest last. */
  log: string[];
  /**
   * The realm's Age, 1..`MAX_AGE`. What century everybody is fighting in.
   *
   * One number for the whole board rather than one per crown, and that is the
   * design rather than a shortcut. An Age in this game is *technological* — it
   * is what a settlement has learned to build — and the strategic map has no
   * settlements to learn in except the player's. A per-crown Age would therefore
   * be a number that only ever moved for one of seventy-four powers, which is
   * not an era, it is a player buff with a grand name.
   *
   * So the realm keeps pace with the player's own capital: the sync that already
   * carries the city's development and its walls out to the map carries its Age
   * too, and every crown's garrisons thicken together. Ages are not a race on
   * this board; they are when the board is set.
   *
   * Read by `ageGarrisonScale` and nothing else today. Hashed and saved, because
   * it changes what a garrison caps at, and a campaign in the Bell is not the
   * same campaign as one in the Ember.
   */
  age: number;
  /** Set once the campaign is decided; nation index, or -1. */
  winner: number;
  over: boolean;
}

// ── Numbers the campaign is balanced around ────────────────────────────────
//
// Every "per turn" below now reads "per month", and **not one of them changed
// value**. That is the point of having made a turn into a month rather than into
// some new unit: upkeep, garrison regrowth, unrest decay, siege duration, camp
// growth and bounty damage all keep the relationships they were tuned to have,
// and the only thing that moved is what fires them — the Long Ledger instead of
// a button. A month is `MONTH_SECONDS`: 280 seconds of simulated time.

/** Garrison a province regrows toward, per fortification level. */
export const GARRISON_BASE = 90;
export const GARRISON_PER_FORT = 70;
/** Points of garrison restored per turn in a quiet, held province. */
export const GARRISON_REGROWTH = 22;

/**
 * What a holding keeps *whatever else has happened to it* — the standing
 * garrison, by what the place is.
 *
 * The owner's complaint was that the board did not distinguish: *"capital and
 * main cities not villages should always have some small garrison"*. It did
 * not. `GARRISON_BASE` was ninety everywhere, so a seat of a crown and a
 * hamlet in the fens capped identically and both could be walked into by
 * anything that happened past with the garrison at five.
 *
 * These are added to the cap *and* held as a floor — see `standingGarrison`
 * and `holdingRank` in `garrisons.ts`. A village gets nothing, on purpose: the
 * whole point of the rule is that it tells the map's places apart.
 */
export const STANDING_GARRISON_CITY = 70;
export const STANDING_GARRISON_SEAT = 130;

/**
 * How much host a holding's walls will take in, per point of garrison cap.
 *
 * The guard against a garrison becoming a free field army. A hamlet cannot
 * swallow a doomstack and a great city can shelter a real force, which is the
 * relationship that makes "get behind the walls" a decision about *which*
 * walls rather than a button that is always right.
 */
export const SHELTER_PER_GARRISON = 3;

/**
 * Gold per strength point to raise an army, and per point per turn to keep it.
 *
 * Upkeep is the number that decides what the campaign feels like, and it is set
 * against the province table deliberately: a nation of three ordinary provinces
 * earns something near forty gold a turn, which buys it roughly eleven hundred
 * points of standing army and nothing else. So an army is a commitment, most
 * ground is held by its garrison rather than by a host, and the question every
 * turn is where the one host you can afford ought to be.
 */
export const RAISE_GOLD_PER_POINT = 0.3;
export const RAISE_IRON_PER_POINT = 0.06;
export const UPKEEP_GOLD_PER_POINT = 0.035;

/** Smallest and largest army the raise command will build. */
export const MIN_ARMY_POINTS = 200;
export const MAX_ARMY_POINTS = 2200;
/** Nobody may field more than this many armies at once. */
export const MAX_ARMIES_PER_NATION = 5;

/** Turns a siege must run before an unassaulted garrison gives in, per fort. */
export const SIEGE_TURNS_PER_FORT = 3;

/** Unrest a freshly taken province starts with, and how fast it settles. */
export const CONQUEST_UNREST = 0.75;
/**
 * What a storming costs the town, as a fraction of what is standing in it.
 *
 * *"you need to rebuild the other town after battle"*. Ground taken by force
 * arrives broken, and this is how broken: a bit under half the roofs down into
 * rubble, the same share of the people gone, the walls knocked about. It is
 * recorded on the province as `ProvinceState.wreck` and paid the next time the
 * owner stands in the place, because at the moment of capture there may be no
 * settlement world here at all.
 *
 * The ceiling matters as much as the level. Ground that changes hands four
 * times in a decade is not four times as ruined as ground taken once — it is a
 * ruin either way, and a town that could be wrecked past `WRECK_MAX` would
 * arrive with nothing standing at all, which is a blank map rather than a
 * rebuilding job. Half a Hearth is a project; no Hearth is a new game.
 */
export const WRECK_ON_STORM = 0.45;
export const WRECK_MAX = 0.75;
export const UNREST_DECAY = 0.09;
/** Provinces a crown may hold before overextension starts to bite. */
export const UNREST_CORE = 6;
/** How much of its demanded margin a host gives up per turn spent idle. */
export const IMPATIENCE_PER_TURN = 0.08;
/** It never gets more reckless than this, however long it waits. */
export const IMPATIENCE_FLOOR = 0.55;
/** How much each province past the core slows the settling of all of them. */
export const UNREST_STRAIN = 0.35;

/** How much a battle win is worth to an army's strength, per victory. */
export const VETERANCY_PER_WIN = 0.06;
export const MAX_VETERANCY = 0.3;

/** The chronicle never grows without bound; a campaign runs for hundreds of turns. */
export const MAX_LOG = 48;

// ── Marching ───────────────────────────────────────────────────────────────

/**
 * How far a nation can see, in hops from ground it holds or stands on.
 *
 * One. Deliberately, and it is not a dial the player is meant to raise: with a
 * scouting range of one hop, every province a hostile host could march *into*
 * your ground from is already visible, and the shortest march on the board takes
 * a full month. Those two facts together give the guarantee the fairness rule
 * actually needs — nothing can arrive on your ground without having been on your
 * map for at least one month, `MONTH_SECONDS` of simulated time, first — and
 * they give it by arithmetic rather than by a lucky die roll on a scouting check.
 *
 * A larger range would buy nothing except a longer list of things happening
 * somewhere else, and would cost the thing that makes the number legible: the
 * player can look at their border and know exactly what they can and cannot see.
 */
export const SCOUT_RANGE = 1;

/**
 * Months a road battle costs each side, as `Army.regroup`.
 *
 * The winner spends the month it fought in and walks on. The loser spends that
 * month and the next: it was beaten and thrown back down a road it had already
 * paid for, and it is not in a state to turn straight round and try again —
 * which, before this existed, is precisely what it did.
 *
 * Two rather than one for the loser is the whole fix, and the number is not
 * arbitrary. Orders are given in `TurnPhase.Council`, *after* `March` has run,
 * so a single month of it expires in time for the very council that would have
 * sent the host back down the same road. Two is the smallest number that puts
 * one clear month between a defeat and the order that answers it.
 */
export const ROAD_BATTLE_DELAY = 1;
export const ROAD_LOSER_REGROUP = 2;

// ── The sea ────────────────────────────────────────────────────────────────
//
// Every number below is set against the land numbers above rather than chosen
// for its own sake, because the only question a navy poses is "instead of
// what". A fleet that is cheaper than the march it replaces makes roads
// pointless; one that is dearer than the army it carries never gets built.

/**
 * Harbour works a coastal province may raise, and the bill for the next level.
 *
 * Three, matching `MAX_FORT`, and priced in timber rather than stone because a
 * wall and a wharf should not compete for the same pile — a crown that has just
 * fortified its border should still be able to afford a harbour, or the two
 * decisions collapse into one decision about stone.
 */
export const MAX_PORT = 3;
export const PORT_GOLD = 100;
export const PORT_TIMBER = 80;
export const PORT_STONE = 30;

/**
 * What a hull is: sixty points of fight, two hundred and sixty of lift.
 *
 * `SHIP_CARRY` is the load-bearing one. `MAX_ARMY_POINTS` is 2,200, so nine
 * hulls carry the largest host in the game — 234 gold and 108 timber against
 * the 660 gold that host cost to raise. A navy is therefore about a third of
 * the price of the thing it moves, which is the ratio that makes it an
 * *enabler* rather than a rival: nobody has to choose between having an army
 * and having a way to use it.
 *
 * `SHIP_POINTS` is set so that the same nine hulls are 540 points at sea —
 * roughly a quarter of the host aboard them. That is deliberately not much.
 * A war fleet is cheap next to an army, so meeting one is affordable, so the
 * sea is genuinely contested rather than a road with weather.
 */
export const SHIP_POINTS = 60;
export const SHIP_CARRY = 260;
export const SHIP_GOLD = 26;
export const SHIP_TIMBER = 12;
/** Gold per hull per month. A twelfth of what the same points of army costs. */
export const SHIP_UPKEEP_GOLD = 1.1;

/** Hulls in one fleet, and fleets one crown may float. */
export const MAX_FLEET_SHIPS = 14;
export const MIN_FLEET_SHIPS = 2;
export const MAX_FLEETS_PER_NATION = 3;

/**
 * Gold a port earns per level, per partner harbour it can reach by lane.
 *
 * Income from a port is *not* a flat stipend, and that is the whole of the
 * design: a harbour is worth what the water around it is worth. A port at the
 * end of a dead-end inlet earns almost nothing; one on the Sundered Sea with
 * six other harbours a fortnight away earns real money — and every one of those
 * partners is a crown that can now be blockaded, raided or courted. Geography
 * decides, which is the same rule the lane network itself was built under.
 *
 * A partner counts only while its owner is not at war with you. That is the
 * lever: taking a port off somebody costs them the port *and* the traffic, and
 * declaring on a trading partner costs you both.
 */
export const PORT_TRADE_GOLD = 2.6;
/** However many harbours are in reach, only this many of them pay. */
export const PORT_TRADE_PARTNER_CAP = 8;

/**
 * Months a sea battle costs the winner, over and above the voyage.
 *
 * Two, where a road battle costs one. A fleet that has just fought is repairing
 * at sea with what it has aboard, and there is no friendly province to limp to.
 */
export const SEA_BATTLE_DELAY = 2;

// ── Bandits ────────────────────────────────────────────────────────────────

/** Strength a fresh camp is founded with, and the most one can grow to. */
export const CAMP_START_STRENGTH = 110;
export const CAMP_MAX_STRENGTH = 420;
/** Points a camp puts on every turn it is left alone. */
export const CAMP_GROWTH = 9;

/** Share of a province's yield that survives a camp sitting on it. */
export const CAMP_YIELD_CHOKE = 0.45;
/** Garrison the camp takes off the province every turn. */
export const CAMP_RAID_GARRISON = 9;

/** Turns a camp must stand before it will put a second camp next door. */
export const CAMP_SPREAD_TURNS = 12;
/** Chance per turn that an old camp spreads, and that unrest breeds a new one. */
export const CAMP_SPREAD_CHANCE = 0.34;
export const CAMP_SEED_CHANCE = 0.16;
/**
 * Camps on the board at once, at most, and how many it opens with.
 *
 * These are *densities* wearing the clothes of counts. Seven and three were a
 * camp on something under a third of a twenty-four-province realm and a camp on
 * an eighth of it at the start — enough that a back country left unpoliced went
 * bad, few enough that the map was dangerous rather than a rout. The board is a
 * hundred and twenty-seven provinces now, and seven camps on it would be a
 * rumour of brigands rather than a policing problem: an empire could swallow
 * twenty provinces and never meet one.
 *
 * Held a little under the old proportion because there are three times as many
 * crowns policing as well, and every one of them sends hosts after camps on its
 * own ground.
 */
export const MAX_CAMPS = 20;
/** Camps the board opens with. */
export const START_CAMPS = 9;

/**
 * Gold a bounty costs, the turns it runs, and the strength it takes off per turn.
 *
 * `BOUNTY_DAMAGE` is set against `CAMP_GROWTH`, not against nothing: a camp puts
 * strength on every turn, so a bounty is worth `BOUNTY_DAMAGE - CAMP_GROWTH` a
 * turn and one purse is just enough to finish a camp that has recently arrived.
 * An old, fat camp needs three or four purses, by which point marching a host at
 * it is both faster and cheaper — which is the trade this is meant to pose.
 */
export const BOUNTY_GOLD = 70;
export const BOUNTY_TURNS = 4;
export const BOUNTY_DAMAGE = 42;

/** Loot for clearing a camp, per point of strength it had. */
export const CAMP_LOOT_GOLD = 0.34;
export const CAMP_LOOT_IRON = 0.05;
